import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

type KanbanColumnProps = {
  title: string;
  count: number;
  children: React.ReactNode;
  /** Column height (the body scrolls internally) */
  height?: number | string;
};

/** 
 * Full-width columns (no gap on the right).
 * Each column grows to fill the row equally.
 * Sticky header + internal vertical scroll for tall content.
 */
export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  title,
  count,
  children,
  height = 'calc(100vh - 180px)',
}) => {
  return (
    <Box
      sx={{
        flex: '1 1 0',        // fill available width evenly
        minWidth: 320,        // responsive min width
        height,
        display: 'flex',
        flexDirection: 'column',
        background: 'background.paper',
        borderRadius: 3,
        border: (t) => `1px dashed ${t.palette.divider}`,
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'background.paper',
          pt: 2,
          pb: 1,
          px: 2,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={800}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {count}
          </Typography>
        </Stack>
      </Box>

      <Stack
        spacing={1.25}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          pb: 2,
        }}
      >
        {children}
      </Stack>
    </Box>
  );
};
